import { ChevronDown, Plus, Building2 } from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem } from "@/components/ui/sidebar";
import { useNavigate } from "react-router";
import { useAppDispatch, useAppSelector } from "@/lib/store";
import { setActiveOrgId } from "@/lib/store/organization.slice";
import { fetchMembership } from "@/lib/store/membership.slice";

export function TeamSwitcher() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { organizations, activeOrgId } = useAppSelector((state) => state.organization);
  const activeOrg = organizations.find((org) => org.id === activeOrgId);

  if (!activeOrg) return null;

  const handleOrgChange = async (orgId: string) => {
    const newOrg = organizations.find((o) => o.id === orgId);
    if (!newOrg) return;

    navigate("/");
    dispatch(setActiveOrgId(orgId));
    await dispatch(fetchMembership());
  };

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild className="w-full">
            <SidebarMenuButton
              className="w-full px-1.5 flex items-center gap-2 justify-between box-shadow-none"
              size={"lg"}
            >
              <div className="flex items-center gap-2">
                <Avatar className="h-8 w-8 rounded-2xl bg-sidebar-primary text-sidebar-primary-foreground">
                  <AvatarImage src={activeOrg.image ?? undefined} alt={activeOrg.display_name} />
                  <AvatarFallback className="rounded-2xl">
                    <Building2 className="size-4" />
                  </AvatarFallback>
                </Avatar>
                <span className="truncate font-semibold hidden md:block">{activeOrg.display_name}</span>
              </div>
              <ChevronDown className="opacity-50" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-64 rounded-lg" align="start" side="bottom" sideOffset={4}>
            <DropdownMenuLabel className="text-xs text-muted-foreground">Organization</DropdownMenuLabel>
            {organizations.map((org) => (
              <DropdownMenuItem
                key={org.id}
                onClick={() => handleOrgChange(org.id)}
                className="gap-2 p-2 flex items-center justify-between"
              >
                <div className="flex items-center gap-2">
                  <Avatar className="h-8 w-8 rounded-2xl">
                    <AvatarImage src={org.image ?? undefined} alt={org.display_name} />
                    <AvatarFallback className="rounded-2xl">
                      <Building2 className="size-4" />
                    </AvatarFallback>
                  </Avatar>
                  {org.display_name}
                </div>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem className="gap-2 p-2">
              <div className="flex size-8 items-center justify-center rounded-2xl border bg-background gap-2">
                <Plus className="size-4" />
              </div>
              <div className="font-medium text-muted-foreground">Add Organization</div>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
